import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import ItinerariesApi from 'libs/api/website/itineraries';
import AuthService from 'services/Auth';

const useItineraries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const [shoppingCenters, setShoppingCenters] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  const navigate = useNavigate();
  const paramsRef = useRef({});

  const checkAuth = useCallback(() => {
    const token = AuthService.getToken();
    if (!token) {
      message.error('You are not authorized. Please login.');
      navigate('/login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchItems = useCallback(async (params = {}) => {
    if (!checkAuth()) return;
    setLoading(true);
    try {
      const response = await ItinerariesApi.list(params);
      if (response.data.success) {
        setItems(response.data.itineraries || []);
        setTotal(response.data.pagination?.total_count || 0);
      } else {
        setItems([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching itineraries:', error);
      message.error(error.response?.data?.message || 'Failed to load itineraries');
    } finally {
      setLoading(false);
    }
  }, [checkAuth]);

  const fetchFilters = useCallback(async () => {
    if (!checkAuth()) return;
    setLoadingCompanies(true);
    try {
      const response = await ItinerariesApi.getFilters();
      const shoppingCentersRaw = response.data.shopping_centers || [];
      setShoppingCenters(shoppingCentersRaw);
    } catch (error) {
      console.error('Error fetching filters:', error);
      message.error('Failed to load filters');
    } finally {
      setLoadingCompanies(false);
    }
  }, [checkAuth]);

  const fetchItem = useCallback(async (id) => {
    if (!checkAuth()) return null;
    try {
      const response = await ItinerariesApi.show(id);
      if (response.data.success) {
        return response.data.itinerary;
      }
      return null;
    } catch (error) {
      console.error('Error fetching itinerary:', error);
      message.error(error.response?.data?.message || 'Failed to load itinerary');
      return null;
    }
  }, [checkAuth]);

  const createItem = useCallback(async (data) => {
    if (!checkAuth()) return null;
    try {
      const response = await ItinerariesApi.create(data);
      if (response.data.success) return response.data.success;
      return null;
    } catch (error) {
      console.error('Error creating itinerary:', error);
      message.error(error.response?.data?.message || 'Failed to create itinerary');
      return null;
    }
  }, [checkAuth]);

  const updateItem = useCallback(async (id, data) => {
    if (!checkAuth()) return null;
    try {
      const response = await ItinerariesApi.update(id, data);
      if (response.data.success) return response.data.itinerary;
      return null;
    } catch (error) {
      console.error('Error updating itinerary:', error);
      message.error(error.response?.data?.message || 'Failed to update itinerary');
      return null;
    }
  }, [checkAuth]);

  const deleteItem = useCallback(async (id) => {
    if (!checkAuth()) return false;
    try {
      await ItinerariesApi.destroy(id);
      message.success('Itinerary deleted successfully');
      return true;
    } catch (error) {
      console.error('Error deleting itinerary:', error);
      message.error(error.response?.data?.message || 'Failed to delete itinerary');
      return false;
    }
  }, [checkAuth]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  useEffect(() => {
    const params = { page: currentPage, per: pageSize };
    Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params[k] = v; });
    paramsRef.current = params;
    fetchItems(params);
  }, [currentPage, pageSize, filters, fetchItems]);

  const updateFilters = useCallback((newFilters) => {
    if (Object.keys(newFilters).length === 0) setFilters({});
    else setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  return {
    items,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    shoppingCenters,
    loadingCompanies,
    updateFilters,
    handlePageChange,
    deleteItem,
    createItem,
    updateItem,
    fetchItem,
    refetch: () => fetchItems(paramsRef.current),
  };
};

export default useItineraries;



